import React from 'react';

/* Runs a card body as a child COMPONENT of the boundary. This is load-bearing:
   JSX written inline as `<Boundary>{expr}</Boundary>` evaluates `expr` in the
   PARENT's render, so a throw there escapes to the parent's boundary (the whole
   page), not this card's. Passing the body as a function that BoundaryChild
   calls during its OWN render keeps the throw inside the boundary's subtree, so
   one card failing can't blank its siblings. */
export function BoundaryChild({ render }) {
  return render();
}

/* One card throwing must never blank the whole Billing page. Each Billing
   section is wrapped in this boundary so a bad payload (e.g. Stripe returning
   rich requirement OBJECTS where the UI expects strings) degrades to an
   inline "unavailable — retry" with the real error text, while every other
   card still renders. Retry resets this boundary and re-runs the section's
   own fetch (onRetry) so a transient 500 can recover without a full reload. */
export default class BillingBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, msg: '' };
  }

  static getDerivedStateFromError(error) {
    return { failed: true, msg: (error && (error.message || String(error))) || 'Unexpected error.' };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[Billing] "${this.props.title || 'section'}" render failed:`, error, info?.componentStack);
  }

  retry = () => {
    this.setState({ failed: false, msg: '' });
    if (typeof this.props.onRetry === 'function') this.props.onRetry();
  };

  render() {
    if (this.state.failed) {
      return (
        <div className="bl-note danger bl-card-error" role="alert">
          <div className="bl-card-error-head">
            <span className="bl-card-error-title">{this.props.title || 'This section is unavailable'}</span>
            <button type="button" className="sv2-btn" onClick={this.retry}>Retry</button>
          </div>
          {this.state.msg ? <div className="bl-card-error-msg">{this.state.msg}</div> : null}
        </div>
      );
    }
    return this.props.children;
  }
}
